import * as THREE from "three/webgpu";
import { PajamaAvatar } from "../PajamaAvatar";
import { CpuBattleSwarm, GpuBattleSwarm, type BattleSwarm, type SwarmFrame } from "./BattleSwarm";
import { BattleTornado } from "./BattleTornado";
import { BattleFlood, BattleRift } from "./BattleCataclysms";
import { BattleCatRush } from "./BattleCatRush";
import { BattleBipedMech, BattleQuadrupedHound } from "./BattleRobotSkills";
import { GpuBattleVfx } from "./GpuBattleVfx";
import { GpuBattleTerrain } from "./GpuBattleTerrain";
import type { BattleTeam } from "./BattleInteractionRouter";

const MATRIX = new THREE.Matrix4();
const POSITION = new THREE.Vector3();
const QUATERNION = new THREE.Quaternion();
const SCALE = new THREE.Vector3();
const EMBER = new THREE.Color(0xff715e);
const AQUA = new THREE.Color(0x55d5c2);
const GOLD = new THREE.Color(0xffdc73);

type Blast = { startedAt: number; x: number; z: number; color: THREE.Color };

export type BattleWorldFrame = SwarmFrame & {
  emberBase: number;
  aquaBase: number;
  emberShield: number;
  aquaShield: number;
};

export class BattleWorld {
  readonly scene = new THREE.Scene();
  readonly camera = new THREE.OrthographicCamera(-5, 5, 8.8, -8.8, 0.1, 80);
  readonly renderer: THREE.WebGPURenderer;
  readonly swarm: BattleSwarm;
  readonly tornado: BattleTornado;
  readonly flood: BattleFlood;
  readonly rift: BattleRift;
  readonly catRush: BattleCatRush;
  readonly biped: BattleBipedMech;
  readonly hound: BattleQuadrupedHound;
  readonly gpuVfx: GpuBattleVfx | null;
  readonly gpuTerrain: GpuBattleTerrain;
  readonly emberCommander: PajamaAvatar;
  readonly aquaCommander: PajamaAvatar;
  readonly #viewport: HTMLElement;
  readonly #frontMarker: THREE.Mesh;
  readonly #emberBase: THREE.Group;
  readonly #aquaBase: THREE.Group;
  readonly #tracers: THREE.InstancedMesh;
  readonly #impactSparks: THREE.InstancedMesh;
  readonly #clashArcs: THREE.InstancedMesh;
  readonly #clashDust: THREE.InstancedMesh;
  readonly #clashWaves: THREE.InstancedMesh;
  readonly #feathers: THREE.InstancedMesh;
  readonly #riftLightning: THREE.LineSegments;
  readonly #riftVeil: THREE.Group;
  readonly #clashSlashes: THREE.InstancedMesh;
  readonly #dreamRams: THREE.InstancedMesh[];
  readonly #shockwaves: THREE.InstancedMesh;
  readonly #blasts: Blast[] = [];
  readonly #disposables: THREE.Object3D[] = [];
  #backend = "initializing";
  #disposed = false;
  #clashEnergy = 0.7;
  #cameraShake = 0;
  #lastFrontline = 0;
  #novaStartedAt = -10;
  #novaDirection = 0;
  readonly #ramChargeStartedAt: Record<BattleTeam, number> = { ember: -10, aqua: -10 };
  readonly #cameraHome = new THREE.Vector3(0, 11.8, -14.5);

  private constructor(viewport: HTMLElement, renderer: THREE.WebGPURenderer, backend: "webgpu" | "webgl2") {
    this.#viewport = viewport;
    this.renderer = renderer;
    this.scene.background = new THREE.Color(0x07182d);
    this.scene.fog = new THREE.FogExp2(0x17344a, 0.022);
    this.camera.position.copy(this.#cameraHome);
    this.camera.lookAt(0, 0.45, 0.55);
    this.scene.add(new THREE.HemisphereLight(0xb8d8ff, 0x302b39, 2.05));
    const key = new THREE.DirectionalLight(0xffe7c4, 3.4);
    key.position.set(-7, 14, -9);
    this.scene.add(key);
    const rim = new THREE.DirectionalLight(0x67d5ff, 2.15);
    rim.position.set(8, 9, 8);
    this.scene.add(rim);

    this.#buildBattlefield();
    this.#emberBase = this.#buildBase("ember");
    this.#aquaBase = this.#buildBase("aqua");
    this.#dreamRams = this.#buildDreamRams();
    this.#frontMarker = new THREE.Mesh(
      new THREE.BoxGeometry(0.075, 0.025, 6.4),
      new THREE.MeshBasicMaterial({ color: 0xffed9a, transparent: true, opacity: 0.68 }),
    );
    this.#frontMarker.position.y = 0.035;
    this.scene.add(this.#frontMarker);
    this.#disposables.push(this.#frontMarker);

    this.swarm = backend === "webgpu" ? new GpuBattleSwarm(this.scene, renderer) : new CpuBattleSwarm(this.scene);
    this.tornado = new BattleTornado(this.scene);
    this.flood = new BattleFlood(this.scene);
    this.rift = new BattleRift(this.scene);
    this.catRush = new BattleCatRush(this.scene);
    this.biped = new BattleBipedMech(this.scene);
    this.hound = new BattleQuadrupedHound(this.scene);
    this.gpuVfx = backend === "webgpu" ? new GpuBattleVfx(this.scene, renderer) : null;
    this.gpuTerrain = new GpuBattleTerrain(this.scene);
    this.emberCommander = new PajamaAvatar(this.scene);
    this.aquaCommander = new PajamaAvatar(this.scene);
    this.emberCommander.root.scale.setScalar(0.72);
    this.aquaCommander.root.scale.setScalar(0.72);
    this.emberCommander.root.position.z = -3.45;
    this.aquaCommander.root.position.z = 3.45;

    this.#tracers = this.#buildTracers();
    this.#impactSparks = this.#buildImpactSparks();
    this.#clashArcs = this.#buildClashArcs();
    this.#clashDust = this.#buildClashDust();
    this.#clashWaves = this.#buildClashWaves();
    this.#feathers = this.#buildFeathers();
    this.#riftLightning = this.#buildRiftLightning();
    this.#riftVeil = this.#buildRiftVeil();
    this.#clashSlashes = this.#buildClashSlashes();
    this.#shockwaves = this.#buildShockwaves();
    this.resize();
  }

  static async create(viewport: HTMLElement): Promise<BattleWorld> {
    const params = new URLSearchParams(location.search);
    const renderer = new THREE.WebGPURenderer({ antialias: true, samples: 4, forceWebGL: params.get("renderer") === "webgl" });
    renderer.setPixelRatio(Math.min(devicePixelRatio || 1, 1.5));
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    renderer.toneMapping = THREE.AgXToneMapping;
    renderer.toneMappingExposure = 1.12;
    viewport.appendChild(renderer.domElement);
    await renderer.init();
    const rendererBackend = (renderer as unknown as { backend?: { isWebGPUBackend?: boolean } }).backend;
    const backend = rendererBackend?.isWebGPUBackend ? "webgpu" : "webgl2";
    const world = new BattleWorld(viewport, renderer, backend);
    world.#backend = backend;
    return world;
  }

  get backend(): string {
    return this.#backend;
  }

  async loadCommanders(): Promise<void> {
    await Promise.all([this.emberCommander.load("bunny"), this.aquaCommander.load("panda"), this.catRush.load()]);
  }

  reset(): void {
    this.#blasts.length = 0;
    this.#clashEnergy = 0.7;
    this.#cameraShake = 0;
    this.#novaStartedAt = -10;
    this.#novaDirection = 0;
    this.#ramChargeStartedAt.ember = -10;
    this.#ramChargeStartedAt.aqua = -10;
    this.#lastFrontline = 0;
    this.gpuVfx?.reset();
    this.gpuTerrain.reset();
    this.emberCommander.run();
    this.aquaCommander.run();
  }

  triggerCharge(team: BattleTeam, time: number): void {
    this.swarm.triggerCharge(team, time);
    this.#ramChargeStartedAt[team] = time;
    this.#clashEnergy = Math.min(2.2, this.#clashEnergy + 0.42);
    this.#cameraShake = Math.max(this.#cameraShake, 0.055);
  }

  triggerSlingshot(team: BattleTeam, time: number, intensity: number): void {
    this.swarm.triggerCharge(team, time);
    this.#ramChargeStartedAt[team] = time;
    this.#clashEnergy = Math.min(2.8, this.#clashEnergy + 1.25 + intensity * 0.65);
    this.#cameraShake = Math.max(this.#cameraShake, 0.11 + intensity * 0.05);
    this.#novaStartedAt = time;
    this.#novaDirection = team === "ember" ? 1 : -1;
  }

  triggerPerfectClash(time: number): void {
    this.swarm.triggerCharge("ember", time);
    this.swarm.triggerCharge("aqua", time);
    this.#clashEnergy = 3.2;
    this.#cameraShake = Math.max(this.#cameraShake, 0.22);
    this.#novaStartedAt = time;
    this.#novaDirection = 0;
    this.#ramChargeStartedAt.ember = time;
    this.#ramChargeStartedAt.aqua = time;
  }

  triggerArtillery(target: BattleTeam, time: number): void {
    const sign = target === "ember" ? -1 : 1;
    this.gpuVfx?.trigger("artillery", sign, time, this.#lastFrontline + sign * 2.15);
    for (let index = 0; index < 5; index += 1) {
      this.#blasts.push({
        startedAt: time + index * 0.08,
        x: sign * (1.1 + ((index * 37) % 100) / 100 * 2.2),
        z: -2.4 + ((index * 61) % 100) / 100 * 4.8,
        color: target === "ember" ? AQUA.clone() : EMBER.clone(),
      });
    }
    this.#clashEnergy = Math.min(2.2, this.#clashEnergy + 0.8);
    this.#cameraShake = Math.max(this.#cameraShake, 0.18);
    if (target === "ember") this.emberCommander.hit();
    else this.aquaCommander.hit();
  }

  triggerTornado(target: BattleTeam, time: number): void {
    const sign = target === "ember" ? -1 : 1;
    this.tornado.trigger(target, time);
    this.swarm.triggerTornado(target, time);
    this.gpuVfx?.trigger("tornado", sign, time, this.#lastFrontline + sign * 1.55);
    this.gpuTerrain.triggerTornado(sign, time);
    this.#cameraShake = Math.max(this.#cameraShake, 0.12);
    if (target === "ember") this.emberCommander.hit();
    else this.aquaCommander.hit();
  }

  triggerFlood(target: BattleTeam, time: number): void {
    const sign = target === "ember" ? -1 : 1;
    this.flood.trigger(target, time);
    this.swarm.triggerFlood(target, time);
    this.gpuVfx?.trigger("flood", sign, time, this.#lastFrontline);
    this.gpuTerrain.triggerFlood(sign, time);
    this.#clashEnergy = Math.min(3, this.#clashEnergy + 1.2);
    this.#cameraShake = Math.max(this.#cameraShake, 0.17);
    if (target === "ember") this.emberCommander.hit();
    else this.aquaCommander.hit();
  }

  triggerRift(target: BattleTeam, time: number): void {
    const sign = target === "ember" ? -1 : 1;
    this.rift.trigger(target, time);
    this.swarm.triggerRift(target, time);
    this.gpuVfx?.trigger("rift", sign, time, this.#lastFrontline + sign * 2.05);
    this.gpuTerrain.triggerRift(sign, time);
    this.#clashEnergy = Math.min(3.2, this.#clashEnergy + 1.5);
    this.#cameraShake = Math.max(this.#cameraShake, 0.23);
    if (target === "ember") this.emberCommander.hit();
    else this.aquaCommander.hit();
  }

  triggerCatRush(attacker: BattleTeam, time: number): void {
    const target: BattleTeam = attacker === "ember" ? "aqua" : "ember";
    const direction = attacker === "ember" ? 1 : -1;
    this.catRush.trigger(attacker, time);
    this.swarm.triggerCatRush(attacker, time);
    this.gpuVfx?.trigger("catrush", direction, time, -direction * 4.7);
    this.#clashEnergy = Math.min(3.2, this.#clashEnergy + 1.45);
    this.#cameraShake = Math.max(this.#cameraShake, 0.2);
    if (target === "ember") this.emberCommander.hit();
    else this.aquaCommander.hit();
  }

  triggerBiped(attacker: BattleTeam, time: number): void {
    const target: BattleTeam = attacker === "ember" ? "aqua" : "ember";
    const direction = attacker === "ember" ? 1 : -1;
    this.biped.trigger(attacker, time);
    this.swarm.triggerBiped(attacker, time);
    this.gpuVfx?.trigger("mech", direction, time, -direction * 4.15);
    this.#clashEnergy = Math.min(3, this.#clashEnergy + 1.1);
    this.#cameraShake = Math.max(this.#cameraShake, 0.14);
    if (target === "ember") this.emberCommander.hit();
    else this.aquaCommander.hit();
  }

  triggerHound(attacker: BattleTeam, time: number): void {
    const target: BattleTeam = attacker === "ember" ? "aqua" : "ember";
    const direction = attacker === "ember" ? 1 : -1;
    this.hound.trigger(attacker, time);
    this.swarm.triggerHound(attacker, time);
    this.gpuVfx?.trigger("hound", direction, time, -direction * 4.45);
    this.#clashEnergy = Math.min(3.2, this.#clashEnergy + 1.4);
    this.#cameraShake = Math.max(this.#cameraShake, 0.2);
    if (target === "ember") this.emberCommander.hit();
    else this.aquaCommander.hit();
  }

  update(dt: number, time: number, frame: BattleWorldFrame): void {
    this.#lastFrontline = frame.frontline;
    this.swarm.update(dt, time, frame);
    this.gpuVfx?.update(dt, time, frame.reducedMotion);
    this.gpuTerrain.update(time, frame.frontline, frame.reducedMotion);
    this.tornado.update(dt, time, frame.frontline, this.camera, frame.reducedMotion);
    this.flood.update(time, frame.frontline, frame.reducedMotion);
    this.rift.update(time, frame.frontline, frame.reducedMotion);
    this.catRush.update(dt, time, frame.frontline, frame.reducedMotion);
    this.biped.update(time, frame.reducedMotion);
    this.hound.update(time, frame.reducedMotion);
    this.#frontMarker.position.x = THREE.MathUtils.damp(this.#frontMarker.position.x, frame.frontline, 8, dt);
    const pulse = 1 + Math.sin(time * 8.2) * (0.08 + this.#clashEnergy * 0.06);
    this.#frontMarker.scale.set(0.8 + this.#clashEnergy * 0.32, 1, pulse);
    this.#updateBase(this.#emberBase, frame.emberBase, frame.emberShield, time);
    this.#updateBase(this.#aquaBase, frame.aquaBase, frame.aquaShield, time);
    this.#updateDreamRams(time, frame.frontline, frame.reducedMotion);
    this.emberCommander.update(dt, time, -4.15, 0.08, frame.emberShield > 0);
    this.aquaCommander.update(dt, time + 0.9, 4.15, 0.08, frame.aquaShield > 0);
    this.emberCommander.root.position.z = -3.45;
    this.aquaCommander.root.position.z = 3.45;
    this.#updateTracers(time, frame.frontline, frame.reducedMotion);
    this.#updateImpactSparks(time, frame.frontline, frame.reducedMotion);
    this.#updateClashEffects(time, frame.frontline, frame.reducedMotion);
    this.#updateFeathers(time, frame.frontline, frame.reducedMotion);
    this.#updateRiftLightning(time, frame.frontline, frame.reducedMotion);
    this.#updateRiftVeil(time, frame.frontline, frame.reducedMotion);
    this.#updateClashSlashes(time, frame.frontline, frame.reducedMotion);
    this.#updateBlasts(time);
    this.#clashEnergy = Math.max(0.55, this.#clashEnergy - dt * 0.42);
    this.#updateCamera(dt, time, frame.frontline, frame.reducedMotion);
  }

  render(): void {
    this.renderer.render(this.scene, this.camera);
  }

  resize(): void {
    const width = Math.max(1, this.#viewport.clientWidth);
    const height = Math.max(1, this.#viewport.clientHeight);
    const aspect = width / height;
    const viewHeight = aspect > 1.2 ? 10.5 : 17.4;
    const viewWidth = viewHeight * aspect;
    this.camera.left = -viewWidth / 2;
    this.camera.right = viewWidth / 2;
    this.camera.top = viewHeight / 2;
    this.camera.bottom = -viewHeight / 2;
    this.camera.updateProjectionMatrix();
    this.renderer.setSize(width, height, false);
  }

  graphicsSnapshot(): { fpsPlaceholder: number; drawCalls: number; computeCalls: number; triangles: number; gpuSimulation: boolean; gpuVfx: { capacity: number; activeParticles: number; computePasses: number }; gpuTerrain: { vertexSimulation: boolean; vertices: number } } {
    const vfx = this.gpuVfx?.snapshot() ?? { capacity: 0, activeParticles: 0, computePasses: 0 };
    return {
      fpsPlaceholder: 0,
      drawCalls: this.renderer.info.render.drawCalls,
      computeCalls: this.renderer.info.compute.frameCalls,
      triangles: this.renderer.info.render.triangles,
      gpuSimulation: this.swarm.gpuSimulation,
      gpuVfx: vfx,
      gpuTerrain: this.gpuTerrain.snapshot(),
    };
  }

  dispose(): void {
    if (this.#disposed) return;
    this.#disposed = true;
    this.swarm.dispose();
    this.tornado.dispose();
    this.flood.dispose();
    this.rift.dispose();
    this.catRush.dispose();
    this.biped.dispose();
    this.hound.dispose();
    this.gpuVfx?.dispose();
    this.gpuTerrain.dispose();
    for (const object of this.#disposables) disposeObject(object);
    for (const object of [this.#emberBase, this.#aquaBase, ...this.#dreamRams, this.#tracers, this.#impactSparks, this.#clashArcs, this.#clashDust, this.#clashWaves, this.#feathers, this.#riftLightning, this.#riftVeil, this.#clashSlashes, this.#shockwaves]) disposeObject(object);
    this.renderer.dispose();
    this.renderer.domElement.remove();
  }

  #buildBattlefield(): void {
    this.#buildRoomBackdrop();
    const ground = new THREE.Mesh(new THREE.PlaneGeometry(11.2, 10.8), new THREE.MeshToonMaterial({ color: 0x344d51 }));
    ground.rotation.x = -Math.PI / 2;
    ground.position.z = 0.4;
    this.scene.add(ground);
    this.#disposables.push(ground);
    const rugBorder = new THREE.Mesh(new THREE.PlaneGeometry(10.1, 7.9), new THREE.MeshToonMaterial({ color: 0xd4b77d }));
    rugBorder.rotation.x = -Math.PI / 2;
    rugBorder.position.y = 0.01;
    this.scene.add(rugBorder);
    this.#disposables.push(rugBorder);
    const left = new THREE.Mesh(new THREE.PlaneGeometry(4.86, 7.55), new THREE.MeshToonMaterial({ color: 0x70424f }));
    left.rotation.x = -Math.PI / 2;
    left.position.set(-2.95, 0.012, 0);
    const right = new THREE.Mesh(new THREE.PlaneGeometry(4.86, 7.55), new THREE.MeshToonMaterial({ color: 0x315c65 }));
    right.rotation.x = -Math.PI / 2;
    right.position.set(2.95, 0.014, 0);
    this.scene.add(left, right);
    this.#disposables.push(left, right);
    for (const z of [-2.2, 0, 2.2]) {
      const lane = new THREE.Mesh(
        new THREE.PlaneGeometry(9.5, 1.18),
        new THREE.MeshToonMaterial({ color: z === 0 ? 0x8e8b78 : 0x7c8175 }),
      );
      lane.rotation.x = -Math.PI / 2;
      lane.position.set(0, 0.025, z);
      this.scene.add(lane);
      this.#disposables.push(lane);
    }
    for (let index = 0; index < 9; index += 1) {
      const stripe = new THREE.Mesh(new THREE.BoxGeometry(0.045, 0.025, 7.35), new THREE.MeshBasicMaterial({ color: index === 4 ? 0xffe59a : 0xcbb78d, transparent: true, opacity: index === 4 ? 0.85 : 0.24 }));
      stripe.position.set(-4.5 + index * 1.125, 0.05, 0);
      this.scene.add(stripe);
      this.#disposables.push(stripe);
    }
    this.#buildPottedPlants();
  }

  #buildRoomBackdrop(): void {
    const room = new THREE.Group();
    room.name = "cozy-night-room";
    const wall = new THREE.Mesh(new THREE.BoxGeometry(11.8, 6.4, 0.24), new THREE.MeshToonMaterial({ color: 0x263b57 }));
    wall.position.set(0, 2.65, 5.25);
    room.add(wall);
    const rail = new THREE.Mesh(new THREE.BoxGeometry(11.8, 0.18, 0.18), new THREE.MeshToonMaterial({ color: 0xd1ae75 }));
    rail.position.set(0, 0.72, 5.05);
    room.add(rail);
    const panes = new THREE.InstancedMesh(new THREE.PlaneGeometry(2.28, 2.08), new THREE.MeshBasicMaterial({ color: 0xffffff, vertexColors: true }), 3);
    const frames = new THREE.InstancedMesh(new THREE.BoxGeometry(1, 1, 0.1), new THREE.MeshToonMaterial({ color: 0xd8bc89 }), 18);
    let frameSlot = 0;
    for (let windowIndex = 0; windowIndex < 3; windowIndex += 1) {
      const x = -3.25 + windowIndex * 3.25;
      MATRIX.compose(POSITION.set(x, 3.35, 5.1), QUATERNION.identity(), SCALE.set(1, 1, 1));
      panes.setMatrixAt(windowIndex, MATRIX);
      panes.setColorAt(windowIndex, new THREE.Color(windowIndex === 1 ? 0x17365e : 0x102c52));
      for (const offset of [-1.18, 1.18]) {
        MATRIX.compose(POSITION.set(x + offset, 3.35, 4.99), QUATERNION.identity(), SCALE.set(0.1, 2.28, 1));
        frames.setMatrixAt(frameSlot, MATRIX);
        frameSlot += 1;
      }
      for (const offset of [-1.09, 1.09]) {
        MATRIX.compose(POSITION.set(x, 3.35 + offset, 4.99), QUATERNION.identity(), SCALE.set(2.48, 0.1, 1));
        frames.setMatrixAt(frameSlot, MATRIX);
        frameSlot += 1;
      }
      MATRIX.compose(POSITION.set(x, 3.35, 4.98), QUATERNION.identity(), SCALE.set(0.07, 2.12, 0.8));
      frames.setMatrixAt(frameSlot, MATRIX);
      frameSlot += 1;
      MATRIX.compose(POSITION.set(x, 3.35, 4.98), QUATERNION.identity(), SCALE.set(2.32, 0.07, 0.8));
      frames.setMatrixAt(frameSlot, MATRIX);
      frameSlot += 1;
    }
    panes.instanceMatrix.needsUpdate = true;
    if (panes.instanceColor) panes.instanceColor.needsUpdate = true;
    frames.instanceMatrix.needsUpdate = true;
    room.add(panes, frames);
    const moon = new THREE.Mesh(
      new THREE.CircleGeometry(0.42, 32),
      new THREE.MeshBasicMaterial({ color: 0xffe6a3, transparent: true, opacity: 0.95 }),
    );
    moon.position.set(3.45, 3.8, 4.94);
    room.add(moon);
    const stars = new THREE.InstancedMesh(new THREE.CircleGeometry(0.025, 5), new THREE.MeshBasicMaterial({ color: 0xffffff, vertexColors: true }), 28);
    for (let index = 0; index < 28; index += 1) {
      const size = index % 5 === 0 ? 1.6 : 0.8;
      MATRIX.compose(POSITION.set(-4.25 + ((index * 73) % 850) / 100, 2.52 + ((index * 47) % 165) / 100, 4.93), QUATERNION.identity(), SCALE.set(size, size, size));
      stars.setMatrixAt(index, MATRIX);
      stars.setColorAt(index, new THREE.Color(index % 4 === 0 ? 0xffe9a8 : 0xaedcff));
    }
    stars.instanceMatrix.needsUpdate = true;
    if (stars.instanceColor) stars.instanceColor.needsUpdate = true;
    room.add(stars);
    const shelf = new THREE.Mesh(new THREE.BoxGeometry(5.2, 0.16, 0.45), new THREE.MeshToonMaterial({ color: 0x7a544f }));
    shelf.position.set(0, 1.55, 4.85);
    room.add(shelf);
    const cushions = new THREE.InstancedMesh(new THREE.IcosahedronGeometry(0.34, 1).scale(1.15, 0.68, 0.48), new THREE.MeshToonMaterial({ color: 0xffffff, vertexColors: true }), 7);
    for (let index = 0; index < 7; index += 1) {
      QUATERNION.setFromEuler(new THREE.Euler(0, 0, (index % 2 ? -1 : 1) * 0.14));
      MATRIX.compose(POSITION.set(-2.15 + index * 0.72, 1.85 + (index % 2) * 0.08, 4.62), QUATERNION, SCALE.set(1, 1, 1));
      cushions.setMatrixAt(index, MATRIX);
      cushions.setColorAt(index, new THREE.Color([0xe9967a, 0x77bfb4, 0xe2c27d][index % 3]));
    }
    cushions.instanceMatrix.needsUpdate = true;
    if (cushions.instanceColor) cushions.instanceColor.needsUpdate = true;
    room.add(cushions);
    this.scene.add(room);
    this.#disposables.push(room);
  }

  #buildPottedPlants(): void {
    const plants = new THREE.Group();
    const pots = new THREE.InstancedMesh(new THREE.CylinderGeometry(0.2, 0.28, 0.38, 7), new THREE.MeshToonMaterial({ color: 0xffffff, vertexColors: true }), 8);
    const stems = new THREE.InstancedMesh(new THREE.CylinderGeometry(0.045, 0.065, 0.58, 6), new THREE.MeshToonMaterial({ color: 0x5e704b }), 8);
    const leavesA = new THREE.InstancedMesh(new THREE.IcosahedronGeometry(0.28, 1).scale(0.72, 1.4, 0.72), new THREE.MeshToonMaterial({ color: 0xffffff, vertexColors: true }), 8);
    const leavesB = new THREE.InstancedMesh(new THREE.IcosahedronGeometry(0.24, 1).scale(0.72, 1.35, 0.72), new THREE.MeshToonMaterial({ color: 0xffffff, vertexColors: true }), 8);
    for (let index = 0; index < 8; index += 1) {
      const side = index % 2 === 0 ? -1 : 1;
      const row = Math.floor(index / 2);
      const x = side * (4.92 - (row % 2) * 0.14);
      const z = -3.4 + row * 2.2;
      MATRIX.compose(POSITION.set(x, 0.19, z), QUATERNION.identity(), SCALE.set(1, 1, 1));
      pots.setMatrixAt(index, MATRIX);
      pots.setColorAt(index, new THREE.Color(side < 0 ? 0x9a5961 : 0x47828a));
      MATRIX.compose(POSITION.set(x, 0.68, z), QUATERNION.identity(), SCALE.set(1, 1, 1));
      stems.setMatrixAt(index, MATRIX);
      QUATERNION.setFromEuler(new THREE.Euler(0, index * 0.7, 0.38));
      MATRIX.compose(POSITION.set(x - 0.15, 1.02, z), QUATERNION, SCALE.set(1, 1, 1));
      leavesA.setMatrixAt(index, MATRIX);
      QUATERNION.setFromEuler(new THREE.Euler(0, index * 0.7, -0.42));
      MATRIX.compose(POSITION.set(x + 0.16, 1.1, z + 0.05), QUATERNION, SCALE.set(1, 1, 1));
      leavesB.setMatrixAt(index, MATRIX);
      const leafColor = new THREE.Color(row % 2 ? 0x4d856c : 0x648958);
      leavesA.setColorAt(index, leafColor);
      leavesB.setColorAt(index, leafColor);
    }
    for (const mesh of [pots, stems, leavesA, leavesB]) {
      mesh.instanceMatrix.needsUpdate = true;
      if (mesh.instanceColor) mesh.instanceColor.needsUpdate = true;
      plants.add(mesh);
    }
    this.scene.add(plants);
    this.#disposables.push(plants);
  }

  #buildBase(team: BattleTeam): THREE.Group {
    const sign = team === "ember" ? -1 : 1;
    const color = team === "ember" ? 0xdc6658 : 0x45aaae;
    const group = new THREE.Group();
    group.name = `${team}-pillow-fort`;
    group.position.set(sign * 4.25, 0, 0);
    const platform = new THREE.Mesh(new THREE.CylinderGeometry(0.96, 1.08, 0.3, 12), new THREE.MeshToonMaterial({ color: 0xead5a5 }));
    platform.position.y = 0.17;
    const keep = new THREE.Mesh(new THREE.BoxGeometry(1.08, 0.92, 1.38), new THREE.MeshToonMaterial({ color }));
    keep.position.y = 0.82;
    const roof = new THREE.Mesh(new THREE.ConeGeometry(1.03, 0.68, 4), new THREE.MeshToonMaterial({ color: team === "ember" ? 0x632f43 : 0x214b69 }));
    roof.position.y = 1.68;
    roof.rotation.y = Math.PI / 4;
    const core = new THREE.Mesh(new THREE.SphereGeometry(0.24, 10, 8), new THREE.MeshToonMaterial({ color: 0xffef94, emissive: color, emissiveIntensity: 0.7 }));
    core.name = "base-core";
    core.position.set(-sign * 0.62, 0.88, 0);
    group.add(platform, keep, roof, core);
    const aura = new THREE.Mesh(
      new THREE.TorusGeometry(0.95, 0.035, 5, 36),
      new THREE.MeshBasicMaterial({ color, transparent: true, opacity: 0.7, depthWrite: false, blending: THREE.AdditiveBlending }),
    );
    aura.name = "base-aura";
    aura.rotation.x = Math.PI / 2;
    aura.position.y = 0.08;
    group.add(aura);
    const pillows = new THREE.InstancedMesh(
      new THREE.IcosahedronGeometry(0.42, 2).scale(1.15, 0.48, 0.82),
      new THREE.MeshToonMaterial({ color: 0xffffff, vertexColors: true }),
      7,
    );
    for (let index = 0; index < 7; index += 1) {
      QUATERNION.setFromEuler(new THREE.Euler((index % 2) * 0.15, index * 0.7, sign * 0.1));
      MATRIX.compose(
        POSITION.set(-sign * (0.48 + (index % 2) * 0.18), 0.36 + Math.floor(index / 3) * 0.27, -1.2 + (index % 3) * 1.2),
        QUATERNION,
        SCALE.set(1, 1, 1),
      );
      pillows.setMatrixAt(index, MATRIX);
      pillows.setColorAt(index, new THREE.Color(index % 3 === 0 ? 0xffedcf : index % 2 ? color : 0xe6c880));
    }
    pillows.instanceMatrix.needsUpdate = true;
    if (pillows.instanceColor) pillows.instanceColor.needsUpdate = true;
    group.add(pillows);
    const pole = new THREE.Mesh(new THREE.CylinderGeometry(0.035, 0.045, 2.15, 7), new THREE.MeshToonMaterial({ color: 0xd8b66b }));
    pole.position.set(sign * 0.15, 1.35, -1.05);
    const flag = new THREE.Mesh(new THREE.BoxGeometry(0.08, 0.55, 0.85), new THREE.MeshToonMaterial({ color }));
    flag.position.set(sign * 0.15, 2.1, -0.67);
    flag.rotation.y = 0.1 * sign;
    group.add(pole, flag);
    for (const z of [-1.65, 1.65]) {
      const turret = new THREE.Group();
      const base = new THREE.Mesh(new THREE.CylinderGeometry(0.25, 0.34, 0.42, 8), new THREE.MeshToonMaterial({ color: 0x3c4b4c }));
      base.position.y = 0.21;
      const barrel = new THREE.Mesh(new THREE.CylinderGeometry(0.075, 0.1, 0.72, 7), new THREE.MeshToonMaterial({ color: 0xd7b46f }));
      barrel.rotation.z = Math.PI / 2;
      barrel.position.set(-sign * 0.28, 0.53, 0);
      turret.add(base, barrel);
      turret.position.set(-sign * 0.3, 0, z);
      group.add(turret);
    }
    this.scene.add(group);
    return group;
  }

  #buildTracers(): THREE.InstancedMesh {
    const mesh = new THREE.InstancedMesh(
      new THREE.SphereGeometry(0.055, 5, 4),
      new THREE.MeshBasicMaterial({ color: 0xffffff, vertexColors: true }),
      96,
    );
    mesh.frustumCulled = false;
    for (let index = 0; index < 96; index += 1) mesh.setColorAt(index, index % 2 ? AQUA : EMBER);
    this.scene.add(mesh);
    return mesh;
  }

  #buildDreamRams(): THREE.InstancedMesh[] {
    const parts: Array<{ geometry: THREE.BufferGeometry; palette: "outline" | "pillow" | "team" | "gold" | "ink"; side?: THREE.Side }> = [
      { geometry: new THREE.IcosahedronGeometry(0.56, 2).scale(1.26, 0.78, 0.84).translate(0, 0.63, 0), palette: "outline", side: THREE.BackSide },
      { geometry: new THREE.IcosahedronGeometry(0.51, 2).scale(1.26, 0.78, 0.84).translate(0, 0.63, 0), palette: "pillow" },
      { geometry: new THREE.IcosahedronGeometry(0.37, 2).scale(1, 0.9, 0.86).translate(0.49, 0.78, 0), palette: "team" },
      { geometry: new THREE.ConeGeometry(0.13, 0.54, 8).rotateZ(-Math.PI / 2).translate(0.79, 0.92, 0), palette: "gold" },
      { geometry: new THREE.BoxGeometry(0.54, 0.075, 0.78).translate(-0.08, 0.98, 0), palette: "team" },
      { geometry: new THREE.BoxGeometry(0.16, 0.055, 0.64).translate(0.68, 0.82, 0), palette: "ink" },
    ];
    return parts.map(({ geometry, palette, side }) => {
      const material = new THREE.MeshToonMaterial({ color: 0xffffff, vertexColors: true, side });
      const mesh = new THREE.InstancedMesh(geometry, material, 2);
      mesh.name = `dream-ram-${palette}`;
      mesh.frustumCulled = false;
      mesh.setColorAt(0, ramColor("ember", palette));
      mesh.setColorAt(1, ramColor("aqua", palette));
      if (mesh.instanceColor) mesh.instanceColor.needsUpdate = true;
      this.scene.add(mesh);
      return mesh;
    });
  }

  #buildImpactSparks(): THREE.InstancedMesh {
    const mesh = new THREE.InstancedMesh(
      new THREE.TetrahedronGeometry(0.095, 0),
      new THREE.MeshBasicMaterial({ color: 0xffffff, vertexColors: true, transparent: true, opacity: 0.95, depthWrite: false, blending: THREE.AdditiveBlending }),
      84,
    );
    mesh.frustumCulled = false;
    for (let index = 0; index < 84; index += 1) mesh.setColorAt(index, index % 3 === 0 ? GOLD : index % 2 ? EMBER : AQUA);
    this.scene.add(mesh);
    return mesh;
  }

  #buildClashArcs(): THREE.InstancedMesh {
    const mesh = new THREE.InstancedMesh(
      new THREE.TorusGeometry(0.28, 0.035, 4, 14, Math.PI * 1.15),
      new THREE.MeshBasicMaterial({ color: 0xffffff, vertexColors: true, transparent: true, opacity: 0.88, depthWrite: false, blending: THREE.AdditiveBlending }),
      36,
    );
    mesh.frustumCulled = false;
    for (let index = 0; index < 36; index += 1) mesh.setColorAt(index, index % 4 === 0 ? GOLD : index % 2 ? EMBER : AQUA);
    this.scene.add(mesh);
    return mesh;
  }

  #buildClashDust(): THREE.InstancedMesh {
    const mesh = new THREE.InstancedMesh(
      new THREE.DodecahedronGeometry(0.09, 0),
      new THREE.MeshToonMaterial({ color: 0xd7c39b, transparent: true, opacity: 0.42, depthWrite: false }),
      42,
    );
    mesh.frustumCulled = false;
    this.scene.add(mesh);
    return mesh;
  }

  #buildClashWaves(): THREE.InstancedMesh {
    const mesh = new THREE.InstancedMesh(
      new THREE.TorusGeometry(0.32, 0.028, 4, 24),
      new THREE.MeshBasicMaterial({ color: 0xffffff, vertexColors: true, transparent: true, opacity: 0.58, depthWrite: false, blending: THREE.AdditiveBlending }),
      12,
    );
    mesh.frustumCulled = false;
    for (let index = 0; index < 12; index += 1) mesh.setColorAt(index, index % 2 ? EMBER : AQUA);
    this.scene.add(mesh);
    return mesh;
  }

  #buildFeathers(): THREE.InstancedMesh {
    const mesh = new THREE.InstancedMesh(
      new THREE.BoxGeometry(0.15, 0.025, 0.065),
      new THREE.MeshBasicMaterial({ color: 0xffffff, vertexColors: true, transparent: true, opacity: 0.9, depthWrite: false }),
      96,
    );
    mesh.frustumCulled = false;
    mesh.count = 0;
    for (let index = 0; index < 96; index += 1) {
      mesh.setColorAt(index, index % 7 === 0 ? GOLD : index % 2 ? new THREE.Color(0xfff1d0) : new THREE.Color(0xcff9ef));
    }
    this.scene.add(mesh);
    return mesh;
  }

  #buildRiftLightning(): THREE.LineSegments {
    const segmentCount = 48;
    const positions = new Float32Array(segmentCount * 2 * 3);
    const colors = new Float32Array(segmentCount * 2 * 3);
    for (let index = 0; index < segmentCount * 2; index += 1) {
      const color = index % 6 < 3 ? AQUA : EMBER;
      colors[index * 3] = color.r;
      colors[index * 3 + 1] = color.g;
      colors[index * 3 + 2] = color.b;
    }
    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute("position", new THREE.BufferAttribute(positions, 3));
    geometry.setAttribute("color", new THREE.BufferAttribute(colors, 3));
    const material = new THREE.LineBasicMaterial({ vertexColors: true, transparent: true, opacity: 0.8, depthWrite: false, blending: THREE.AdditiveBlending });
    const lines = new THREE.LineSegments(geometry, material);
    lines.frustumCulled = false;
    lines.renderOrder = 7;
    this.scene.add(lines);
    return lines;
  }

  #buildRiftVeil(): THREE.Group {
    const group = new THREE.Group();
    group.name = "dream-seam-energy-veil";
    const aqua = new THREE.Mesh(
      new THREE.BoxGeometry(0.12, 0.72, 6.25),
      new THREE.MeshBasicMaterial({ color: AQUA, transparent: true, opacity: 0.12, depthWrite: false, blending: THREE.AdditiveBlending }),
    );
    const ember = new THREE.Mesh(
      new THREE.BoxGeometry(0.12, 0.72, 6.25),
      new THREE.MeshBasicMaterial({ color: EMBER, transparent: true, opacity: 0.12, depthWrite: false, blending: THREE.AdditiveBlending }),
    );
    aqua.position.x = 0.065;
    ember.position.x = -0.065;
    group.add(aqua, ember);
    this.scene.add(group);
    return group;
  }

  #buildClashSlashes(): THREE.InstancedMesh {
    const mesh = new THREE.InstancedMesh(
      new THREE.BoxGeometry(0.92, 0.035, 0.028),
      new THREE.MeshBasicMaterial({ color: 0xffffff, vertexColors: true, transparent: true, opacity: 0.84, depthWrite: false, blending: THREE.AdditiveBlending }),
      28,
    );
    mesh.frustumCulled = false;
    for (let index = 0; index < 28; index += 1) mesh.setColorAt(index, index % 5 === 0 ? GOLD : index % 2 ? EMBER : AQUA);
    this.scene.add(mesh);
    return mesh;
  }

  #buildShockwaves(): THREE.InstancedMesh {
    const mesh = new THREE.InstancedMesh(
      new THREE.TorusGeometry(0.18, 0.035, 5, 28),
      new THREE.MeshBasicMaterial({ color: 0xfff1bd, transparent: true, opacity: 0.78, depthWrite: false, blending: THREE.AdditiveBlending }),
      24,
    );
    mesh.frustumCulled = false;
    mesh.count = 0;
    this.scene.add(mesh);
    return mesh;
  }

  #updateBase(group: THREE.Group, health: number, shield: number, time: number): void {
    const ratio = THREE.MathUtils.clamp(health / 1_000, 0, 1);
    group.rotation.z = Math.sin(time * 2.4 + group.position.x) * (1 - ratio) * 0.04;
    group.scale.y = 0.86 + ratio * 0.14;
    const core = group.getObjectByName("base-core") as THREE.Mesh;
    const material = core.material as THREE.MeshToonMaterial;
    material.emissiveIntensity = 0.45 + ratio * 0.45 + (shield > 0 ? Math.sin(time * 9) * 0.25 + 0.4 : 0);
    const aura = group.getObjectByName("base-aura") as THREE.Mesh;
    aura.scale.setScalar(1 + Math.sin(time * 5 + group.position.x) * 0.06 + (shield > 0 ? 0.18 : 0));
  }

  #updateDreamRams(time: number, frontline: number, reducedMotion: boolean): void {
    for (const [slot, team] of (["ember", "aqua"] as BattleTeam[]).entries()) {
      const sign = team === "ember" ? -1 : 1;
      const age = time - this.#ramChargeStartedAt[team];
      const charge = age >= 0 && age < 0.78 ? Math.sin(age / 0.78 * Math.PI) : 0;
      const impactBounce = reducedMotion ? 0 : Math.abs(Math.sin(time * 5.8 + slot * 1.7)) * 0.06;
      POSITION.set(frontline + sign * (0.96 - charge * 0.62), 0.02 + impactBounce + charge * 0.08, team === "ember" ? -1.08 : 1.08);
      QUATERNION.setFromEuler(new THREE.Euler(0, team === "ember" ? 0 : Math.PI, sign * (charge * 0.16 - 0.04)));
      SCALE.setScalar(0.72 + charge * 0.09);
      MATRIX.compose(POSITION, QUATERNION, SCALE);
      for (const mesh of this.#dreamRams) mesh.setMatrixAt(slot, MATRIX);
    }
    for (const mesh of this.#dreamRams) mesh.instanceMatrix.needsUpdate = true;
  }

  #updateTracers(time: number, frontline: number, reducedMotion: boolean): void {
    const count = reducedMotion ? 36 : 96;
    this.#tracers.count = count;
    for (let index = 0; index < count; index += 1) {
      const side = index % 2 ? 1 : -1;
      const seed = ((index * 67) % 101) / 101;
      const phase = (time * (0.55 + seed * 0.6) + seed) % 1;
      const startX = side * 3.75;
      const endX = frontline + side * 0.08;
      POSITION.set(THREE.MathUtils.lerp(startX, endX, phase), 0.45 + Math.sin(phase * Math.PI) * (0.5 + seed), Math.sin(seed * 37) * 2.55);
      SCALE.setScalar(0.65 + seed * 1.2);
      MATRIX.compose(POSITION, QUATERNION.identity(), SCALE);
      this.#tracers.setMatrixAt(index, MATRIX);
    }
    this.#tracers.instanceMatrix.needsUpdate = true;
  }

  #updateImpactSparks(time: number, frontline: number, reducedMotion: boolean): void {
    const count = reducedMotion ? 24 : 84;
    this.#impactSparks.count = count;
    for (let index = 0; index < count; index += 1) {
      const seed = ((index * 43) % 97) / 97;
      const phase = (time * (1.8 + seed) + seed * 3) % 1;
      const radius = phase * 0.55;
      POSITION.set(frontline + Math.cos(seed * 31) * radius, 0.24 + Math.sin(phase * Math.PI) * 0.62, Math.sin(seed * 47) * 2.6 + Math.cos(seed * 19) * radius);
      QUATERNION.setFromEuler(new THREE.Euler(time + seed, seed * 8, time * 1.3));
      SCALE.setScalar((1 - phase) * (0.55 + seed));
      MATRIX.compose(POSITION, QUATERNION, SCALE);
      this.#impactSparks.setMatrixAt(index, MATRIX);
    }
    this.#impactSparks.instanceMatrix.needsUpdate = true;
  }

  #updateClashEffects(time: number, frontline: number, reducedMotion: boolean): void {
    const energy = reducedMotion ? 0.35 : this.#clashEnergy;
    const arcCount = reducedMotion ? 12 : 36;
    this.#clashArcs.count = arcCount;
    for (let index = 0; index < arcCount; index += 1) {
      const seed = ((index * 71) % 103) / 103;
      const phase = (time * (2.1 + seed * 1.8) + seed * 8.7) % 1;
      const lane = [-2.2, 0, 2.2][index % 3]!;
      POSITION.set(frontline + Math.sin(seed * 44 + time * 9) * 0.18, 0.25 + Math.sin(phase * Math.PI) * (0.35 + seed * 0.45), lane + Math.sin(seed * 91) * 0.62);
      QUATERNION.setFromEuler(new THREE.Euler(seed * 0.5, time * (index % 2 ? -0.8 : 0.8), seed * Math.PI * 2));
      SCALE.setScalar((0.35 + (1 - phase) * 1.05) * energy);
      MATRIX.compose(POSITION, QUATERNION, SCALE);
      this.#clashArcs.setMatrixAt(index, MATRIX);
    }
    this.#clashArcs.instanceMatrix.needsUpdate = true;

    const dustCount = reducedMotion ? 14 : 42;
    this.#clashDust.count = dustCount;
    for (let index = 0; index < dustCount; index += 1) {
      const seed = ((index * 53) % 109) / 109;
      const phase = (time * (0.7 + seed * 0.8) + seed * 6) % 1;
      POSITION.set(frontline + Math.sin(seed * 27) * phase * 0.62, 0.07 + Math.sin(phase * Math.PI) * 0.28, (seed * 2 - 1) * 3.05);
      QUATERNION.setFromEuler(new THREE.Euler(seed * 7 + time, time * 0.4, seed * 11));
      SCALE.setScalar((0.2 + Math.sin(phase * Math.PI) * 1.2) * energy);
      MATRIX.compose(POSITION, QUATERNION, SCALE);
      this.#clashDust.setMatrixAt(index, MATRIX);
    }
    this.#clashDust.instanceMatrix.needsUpdate = true;

    const waveCount = reducedMotion ? 3 : 12;
    this.#clashWaves.count = waveCount;
    for (let index = 0; index < waveCount; index += 1) {
      const phase = (time * (1.3 + (index % 3) * 0.18) + index * 0.27) % 1;
      POSITION.set(frontline + (index % 2 ? -0.06 : 0.06), 0.075, [-2.2, 0, 2.2][index % 3]!);
      QUATERNION.setFromEuler(new THREE.Euler(Math.PI / 2, 0, index));
      SCALE.setScalar((0.15 + phase * 1.45) * energy);
      MATRIX.compose(POSITION, QUATERNION, SCALE);
      this.#clashWaves.setMatrixAt(index, MATRIX);
    }
    this.#clashWaves.instanceMatrix.needsUpdate = true;
  }

  #updateFeathers(time: number, frontline: number, reducedMotion: boolean): void {
    const novaAge = time - this.#novaStartedAt;
    const novaActive = novaAge >= 0 && novaAge < 1.65;
    const count = novaActive ? (reducedMotion ? 36 : 96) : (reducedMotion ? 5 : 18);
    this.#feathers.count = count;
    for (let index = 0; index < count; index += 1) {
      const seed = ((index * 79) % 101) / 101;
      const phase = novaActive ? THREE.MathUtils.clamp(novaAge / 1.65, 0, 1) : (time * (0.34 + seed * 0.22) + seed * 8) % 1;
      const direction = this.#novaDirection === 0 ? (index % 2 ? -1 : 1) : this.#novaDirection;
      const travel = novaActive ? phase * (0.75 + seed * 2.25) : Math.sin(seed * 21 + time) * 0.18;
      POSITION.set(
        frontline + direction * travel + Math.sin(seed * 45 + phase * 8) * 0.22,
        0.18 + Math.sin(phase * Math.PI) * (novaActive ? 1.4 + seed * 2.2 : 0.55),
        (seed * 2 - 1) * 3.15 + Math.cos(seed * 37 + phase * 9) * 0.36,
      );
      QUATERNION.setFromEuler(new THREE.Euler(time * (1.5 + seed), phase * 12 + seed * 4, seed * 9));
      const envelope = novaActive ? Math.sin(phase * Math.PI) : 0.45 + Math.sin(phase * Math.PI) * 0.55;
      SCALE.setScalar(Math.max(0.01, envelope * (0.65 + seed * 1.15)));
      MATRIX.compose(POSITION, QUATERNION, SCALE);
      this.#feathers.setMatrixAt(index, MATRIX);
    }
    this.#feathers.instanceMatrix.needsUpdate = true;
  }

  #updateRiftLightning(time: number, frontline: number, reducedMotion: boolean): void {
    const positions = this.#riftLightning.geometry.getAttribute("position") as THREE.BufferAttribute;
    const array = positions.array as Float32Array;
    const amplitude = (reducedMotion ? 0.035 : 0.085) * (0.75 + this.#clashEnergy * 0.3);
    for (let index = 0; index < 48; index += 1) {
      const branch = Math.floor(index / 16);
      const stepIndex = index % 16;
      const z = -3.05 + stepIndex / 15 * 6.1;
      const nextZ = -3.05 + Math.min(15, stepIndex + 1) / 15 * 6.1;
      const base = index * 6;
      array[base] = frontline + Math.sin(time * (18 + branch * 4) + stepIndex * 4.7 + branch) * amplitude * (branch + 1);
      array[base + 1] = 0.12 + branch * 0.09 + Math.abs(Math.sin(time * 13 + stepIndex)) * 0.13;
      array[base + 2] = z;
      array[base + 3] = frontline + Math.sin(time * (21 + branch * 3) + (stepIndex + 1) * 4.7 + branch) * amplitude * (branch + 1);
      array[base + 4] = 0.12 + branch * 0.09 + Math.abs(Math.sin(time * 13 + stepIndex + 1)) * 0.13;
      array[base + 5] = nextZ;
    }
    positions.needsUpdate = true;
    (this.#riftLightning.material as THREE.LineBasicMaterial).opacity = Math.min(1, 0.48 + this.#clashEnergy * 0.22);
  }

  #updateRiftVeil(time: number, frontline: number, reducedMotion: boolean): void {
    this.#riftVeil.position.set(frontline, 0.38, 0);
    const energy = reducedMotion ? 0.45 : this.#clashEnergy;
    this.#riftVeil.scale.set(0.8 + Math.sin(time * 15) * 0.24 + energy * 0.22, 0.65 + energy * 0.38, 1 + Math.sin(time * 6) * 0.025);
    for (const child of this.#riftVeil.children) {
      const material = (child as THREE.Mesh).material as THREE.MeshBasicMaterial;
      material.opacity = Math.min(0.36, 0.055 + energy * 0.075 + Math.sin(time * 19 + child.position.x) * 0.025);
    }
  }

  #updateClashSlashes(time: number, frontline: number, reducedMotion: boolean): void {
    const count = reducedMotion ? 8 : 28;
    this.#clashSlashes.count = count;
    const novaAge = time - this.#novaStartedAt;
    const novaBoost = novaAge >= 0 && novaAge < 0.9 ? 1.7 : 1;
    for (let index = 0; index < count; index += 1) {
      const seed = ((index * 61) % 107) / 107;
      const phase = (time * (2.8 + seed * 2.2) + seed * 13) % 1;
      const lane = [-2.2, 0, 2.2][index % 3]!;
      POSITION.set(frontline + Math.sin(seed * 55 + time * 10) * 0.18, 0.32 + seed * 0.95, lane + Math.sin(seed * 71) * 0.72);
      QUATERNION.setFromEuler(new THREE.Euler(seed * 0.18, seed * 0.25, (index % 2 ? -1 : 1) * (0.48 + seed * 0.62)));
      const flash = Math.sin(phase * Math.PI);
      SCALE.set((0.45 + flash * 1.35) * novaBoost * this.#clashEnergy * 0.55, 0.8 + flash * 0.5, 1);
      MATRIX.compose(POSITION, QUATERNION, SCALE);
      this.#clashSlashes.setMatrixAt(index, MATRIX);
    }
    this.#clashSlashes.instanceMatrix.needsUpdate = true;
  }

  #updateCamera(dt: number, time: number, frontline: number, reducedMotion: boolean): void {
    this.#cameraShake = Math.max(0, this.#cameraShake - dt * 0.42);
    const shake = reducedMotion ? 0 : this.#cameraShake;
    this.camera.position.set(
      this.#cameraHome.x + Math.sin(time * 73) * shake,
      this.#cameraHome.y + Math.sin(time * 59 + 1.4) * shake * 0.45,
      this.#cameraHome.z + Math.sin(time * 67 + 2.1) * shake * 0.35,
    );
    this.camera.lookAt(frontline * 0.035, 0.45, 0.55);
  }

  #updateBlasts(time: number): void {
    while (this.#blasts.length > 8 && time - this.#blasts[0]!.startedAt > 1.2) this.#blasts.shift();
    let count = 0;
    for (const blast of this.#blasts) {
      const age = time - blast.startedAt;
      if (age < 0 || age > 1.2) continue;
      const progress = age / 1.2;
      for (let ring = 0; ring < 3; ring += 1) {
        POSITION.set(blast.x, 0.12 + ring * 0.06, blast.z);
        QUATERNION.setFromEuler(new THREE.Euler(Math.PI / 2, 0, ring * 0.7));
        const radius = 0.35 + progress * (1.1 + ring * 0.28);
        SCALE.setScalar(radius);
        MATRIX.compose(POSITION, QUATERNION, SCALE);
        this.#shockwaves.setMatrixAt(count, MATRIX);
        this.#shockwaves.setColorAt(count, blast.color);
        count += 1;
      }
    }
    this.#shockwaves.count = count;
    this.#shockwaves.instanceMatrix.needsUpdate = true;
    if (this.#shockwaves.instanceColor) this.#shockwaves.instanceColor.needsUpdate = true;
  }
}

function disposeObject(object: THREE.Object3D): void {
  object.removeFromParent();
  object.traverse((child) => {
    const renderable = child as THREE.Mesh | THREE.LineSegments;
    if (!(renderable as THREE.Mesh).isMesh && !(renderable as THREE.LineSegments).isLineSegments) return;
    renderable.geometry.dispose();
    const materials = Array.isArray(renderable.material) ? renderable.material : [renderable.material];
    materials.forEach((material) => material.dispose());
  });
}

function ramColor(team: BattleTeam, palette: "outline" | "pillow" | "team" | "gold" | "ink"): THREE.Color {
  if (palette === "outline") return new THREE.Color(0x172430);
  if (palette === "pillow") return new THREE.Color(team === "ember" ? 0xffe6c7 : 0xd8fff5);
  if (palette === "gold") return new THREE.Color(0xffd66f);
  if (palette === "ink") return new THREE.Color(0x162833);
  return new THREE.Color(team === "ember" ? 0xff715e : 0x55d5c2);
}
